import * as React from "react";
import {cx} from "emotion";

import "./step-1.scss";

import {bindInput}      from "@Common/react/bind-input";
import {cs}             from "@Common/react/chain-services";
import {consumeContext} from "@Common/react/context";
import {UseState}       from "@Common/react/use-state";
import {isDevQuan}      from "@Common/ui-components/debug/is-dev-quan";
import {SliderCheckbox} from "@Common/ui-components/slider-checkbox/slider-checkbox";

export const Step1 = ({
    cardHolderName=undefined,
    cardPrefill=undefined,
    makeDefault=undefined,
    next: rootNext,
    recaptchaToken,
}) => cs(
    ["savedCard", (_, next) => UseState({initValue: cardPrefill && {prefill: cardPrefill}, next})],
    consumeContext("braintree"),
    ["braintreeForm", ({braintree}, next) => braintree.renderForm({next, recaptchaToken})],

    ["makeDefault", (_, next) => UseState({initValue: makeDefault ?? true, next})],
    ["cardHolderName", (_, next) => UseState({initValue: cardHolderName, next})],
    ["showErrors", (_, next) => UseState({next})],
    ["submitting", (_, next) => UseState({next})],
    ["braintreeError", (_, next) => UseState({next})],
    ({braintreeForm, savedCard, submitting, makeDefault, braintreeError, cardHolderName, showErrors}) => rootNext({
        action: {
            label: submitting.value ? "Saving..." : "Next",
            disabled: submitting.value || !cardHolderName.value || !braintreeForm.isValid(),
            clickedWhenDisabled: () => {
                showErrors.onChange(true);
            },
            onClick: async () => {
                try {
                    submitting.onChange(true);
                    const btTokenResult = await braintreeForm.tokenize();
                    savedCard.onChange({
                        nonce: btTokenResult.nonce,
                        prefill: {
                            "number": btTokenResult.details.bin,
                            "expirationDate": `${btTokenResult.details.expirationMonth}/${btTokenResult.details.expirationYear.substring(2)}`,
                        },
                    });
                } catch (e) {
                    braintreeError.onChange(e.toString());
                } finally {
                    submitting.onChange(false);
                }
            },
        },
        ready: isDevQuan() || (cardHolderName.value && savedCard.value?.nonce),
        modCard: (_) => ({
            ..._,
            cardHolderName: cardHolderName.value,
            "braintreeNonce" : savedCard.value.nonce,
            "makeDefault" : makeDefault.value,
        }),
        render: () => {
            const renderBtField = ({id, label}) => (
                <div className={cx("form-group", {hasError: showErrors.value && !braintreeForm.getFieldState(id)?.valid})}>
                    <div className="control-label" xd-qa-type="label" xd-qa-name={label}>
                        {label}
                    </div>
                    <div
                        className="form-input"
                        id={id}
                        xd-qa-type="text_field"
                        xd-qa-name={label}
                    />
                </div>
            );

            const renderFieldI = ({state, label, placeholder, xdQaName, inputProps=undefined}) => (
                <div className={cx("form-group", {hasError: showErrors.value && !state.value})}>
                    <div className="control-label">
                        {label}
                    </div>
                    <input {...{
                        ...inputProps,
                        placeholder,
                        "xd-qa-type": "text_field",
                        "xd-qa-name": xdQaName,
                        ...bindInput(state),
                    }}/>
                </div>
            );

            return (
                <div className="step1-879">
                    {braintreeForm.initFields({
                        getSelector: (name) => `.step1-879 #${name}`,
                        prefills: savedCard.value?.prefill,
                    })}

                    <div className="image">
                        <img
                            src={
                                braintreeForm.getFieldState("cvv")?.focused ? (
                                    require("./card_back.webp")
                                ) : (
                                    require("./visa.webp")
                                )
                            }
                            alt={
                                braintreeForm.getFieldState("cvv")?.focused ? (
                                    "Card Back"
                                ) : (
                                    "Visa"
                                )
                            }
                            xd-qa-type="icon"
                            xd-qa-name="Credit Card Image"
                        />
                    </div>

                    <div className="form">

                        {braintreeForm.loading && (
                            <div className="loading" xd-qa-type="label" xd-qa-name="Loading">
                                <i className="fa fa-spinner fa-pulse"/>
                                Loading Braintree...
                            </div>
                        )}

                        {braintreeForm.initException && (
                            <div className="loading-error">
                                <b>
                                    Error initializing payment processor
                                    {braintreeForm.initException.message && ` (${braintreeForm.initException.message})`}
                                </b>.{" "}
                                Please refresh your browser and try again.
                            </div>
                        )}

                        {renderBtField({
                            id: "number",
                            label: "Card Number",
                        })}

                        {renderFieldI({
                            state: cardHolderName,
                            label: "Name on Card",
                            placeholder: "The name that appears in the card",
                            xdQaName: "NAME",
                            inputProps: {
                                maxLength: 175,
                            },
                        })}

                        <div className="wrap-line">
                            {renderBtField({
                                id: "expirationDate",
                                label: "Expiration Date",
                            })}

                            {renderBtField({
                                id: "cvv",
                                label: "CVV Code",
                            })}
                        </div>

                        <div className="default">
                            <div className="label" xd-qa-type="label" xd-qa-name="Set as default payment method">
                                Set as default payment method
                            </div>
                            {SliderCheckbox({
                                ...makeDefault,
                                xdQaName: "Default Payment Checkbox"
                            })}
                        </div>

                        {braintreeError.value && (
                            <div className="error">
                                {braintreeError.value}
                            </div>
                        )}

                    </div>

                </div>
            );
        },
    }),
);
